import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import DateSelect from "components/custom-ui/DateSelect";
import StatesCustomSelect from "components/custom-ui/StatesCustomSelect";
import UIRadioGroup, { UIRadio } from "components/custom-ui/UIRadioGroup";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow, HorizontalFormRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton, UICheckbox, UIPhoneNumberInput, UITextInput } from "components/ui";
import UIPasswordInput from "components/ui/UIPasswordInput";
import Classes, { UserAccount } from "model";
import React from "react";

import "./styles.css";
import "./mobile.css";


/**
 * @function UserDetailsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for UserDetailsPage
 */
export default function UserDetailsPage(props) {
    const navigate = useRoute(); 
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };
    const { prevStep, goStep, nextStep } = useWorkflow() ?? {};
  
    // An payee id was passed, so get the payee from the model to edit it.
    if(data.isEditing === true && data.selectedUserAccountID !== undefined) {
      data.userAccount = UserAccount.initByID(data.selectedUserAccountID);
      // Since the "date" prop is defined as a String type in "UserAccount"
      // (since undefined is needed by default on this page which BCMSDate does not have)
      // "UserAccount.initByID" breaks the page after an edit. Account for that here.
      const birthDate = data.userAccount.birthDate ?? {};
      const hasDateProp = birthDate._date === undefined ? false : true;
      data.userAccount.birthDate = hasDateProp === true ? BCMSDate.init(new Date(birthDate._date)) : birthDate;
    }
  
    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onBlur: prop => value => {
            let fullName = data.userAccount.fullName ?? "";

            if(prop === "first") {
                fullName = `${ value } ${ fullName }`;
            } else if(prop === "last") {
                fullName = `${ fullName } ${ value }`;
            }
            
            BCMSStateManagerCRUD.init(state)
                    .update([ "data", "userAccount", prop ], value)
                    .update([ "data", "userAccount", "fullName" ], fullName)
                    .submit(setState);
        },
  
        onChange: prop => value => {
          BCMSStateManagerCRUD.init(state)
                .update([ "data", "userAccount", prop ], value)
                .submit(setState);
        },

        onChangePhoneNumber: prop => value => {
            const fullNumber = (value.firstInputVal ?? "") + (value.secondInputVal ?? "") + (value.thirdInputVal ?? "");
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "userAccount", prop ], fullNumber)
                .submit(setState);
        },
  
        cancel: () => goStep(0),

        onSave: () => {
            const defaultUser = BCMSDefaultModel.userAccount();
            const mergedUser = BCMSDefaultModel.mergeRecords(data.userAccount.record, defaultUser.record);
            // "date" values are never empty during a merge so manually set it here.
            mergedUser.lastLogin = defaultUser.lastLogin;
            const nextUserID = UserAccount.nextID();
            const createdUser = UserAccount.init({ ...mergedUser, id: nextUserID });
            
            BCMSRecord.batch([
                UserAccount.create({ ...createdUser.record }),
            ], globalController, Classes);

            window.scrollTo(0, 0);
            nextStep({ data: {...data, userAccount: createdUser }, ui });
        }
    });

    return (
        <PageContainer className="user-details-page" state={ { data, ui } } controller={ controller }>
            <PageContent/>
        </PageContainer>
    )
}

export const getDefaultData = () => ({
    isEditing: false,
    selectedUserAccountID: undefined,
    userAccount: UserAccount.init({ status: "Active" })
  });
  
  export const getDefaultUI = () => ({
    // no ui changes for now
  });

  function PageContent(props) {
    const { data, cancel, onBlur, onChange, onChangePhoneNumber, onSave } = useStateManager();
    const userAccount = data.userAccount ?? {};


    return (
    <PageContentView>
        <CenterContentContainer>
            <ContentHeaderView text="User Details" />
            <div className="flex-container flex-horizontal forms">
                <FormContainer className="left-form-container small-form-width">
                    <FormRowView className="user-id-row" text="*User ID">
                        <UITextInput value={ userAccount.userID } onBlur={ onBlur("userID") } dataPoint="User_ID_Input" />
                    </FormRowView>
                    { data.isEditing &&
                    <FormRowView className="user-id-row">
                        <UICheckbox label="Reset Password" value={ userAccount.resetPassword === true ? "checked" : "" }
                        onChange={ onChange("resetPassword") } />
                    </FormRowView>
                    }
                    { data.isEditing && userAccount.resetPassword === true &&
                    <>
                        <FormRowView className="temporary-password-row" text="*Temporary Password">
                            <UIPasswordInput value={ userAccount.password } onChange={ onChange("password") } dataPoint="Temporary_Password_Input" />
                        </FormRowView>
                        <FormRowView className="confirm-password-row" text="*Confirm Password">
                            <UIPasswordInput value={ userAccount.confirmPassword } onChange={ onChange("confirmPassword") } dataPoint="Confirm_Password_Input" />
                        </FormRowView>
                    </>
                    }
                    { !data.isEditing &&
                    <>
                        <FormRowView className="temporary-password-row" text="*Temporary Password">
                            <UIPasswordInput value={ userAccount.password } onChange={ onChange("password") } dataPoint="Temporary_Password_Input" />
                        </FormRowView>
                        <FormRowView className="confirm-password-row" text="*Confirm Password">
                            <UIPasswordInput value={ userAccount.confirmPassword } onChange={ onChange("confirmPassword") } dataPoint="Confirm_Password_Input" />
                        </FormRowView>
                    </> 
                    }
                    <FormRowView className="status-row" text="Status">
                        <UIRadioGroup direction="horizontal" value={ userAccount.status } onChange={ onChange("status") }>
                            <UIRadio name="status" label="Active" value="Active" className="active-status-ui-radio"  />
                            <UIRadio name="status" label="Disabled" value="Disabled" className="disabled-status-ui-radio" />
                            <UIRadio name="status" label="Locked" value="Locked" className="locked-status-ui-radio disabled" />
                        </UIRadioGroup>
                    </FormRowView>
                    <p className="sub-header">
                        <span data-point="Personal_Information_Label">Personal Information</span>
                    </p>
                    <FormRowView className="title-row" text="Title">
                        <UITextInput value={ userAccount.title } onBlur={ onBlur("title") } />
                    </FormRowView>
                    <FormRowView className="first-name-row" text="*First Name">
                        <UITextInput value={ userAccount.first } onBlur={ onBlur("first") } />
                    </FormRowView>
                    <FormRowView className="middle-name-row" text="Middle Name">
                        <UITextInput value={ userAccount.middleName } onBlur={ onBlur("middleName") } />
                    </FormRowView>
                    <FormRowView className="last-name-row" text="*Last Name">
                        <UITextInput value={ userAccount.last } onBlur={ onBlur("last") } />
                    </FormRowView>
                    <FormRowView className="suffix-row" text="Suffix">
                        <UITextInput value={ userAccount.suffix } onBlur={ onBlur("suffix") } />
                    </FormRowView>
                    <FormRowView className="birth-date-row" text="Birth Date">
                        <DateSelect value={ userAccount.birthDate } enablePast={ true } enableFuture={ false } onChange={ onChange("birthDate") } />
                    </FormRowView>
                    <FormRowView className="social-security-number-row" text="Social Security Number">
                        <UITextInput value={ userAccount.ssn } onBlur={ onBlur("ssn") } />
                    </FormRowView>
                </FormContainer>
                <FormContainer className="right-form-container small-form-width">
                    <p className="sub-header contact-information-header">
                        <span data-point="Contact_Information_Label">Contact Information</span>
                    </p>
                    <FormRowView className="email-address-row" text="*Email Address">
                        <UITextInput value={ userAccount.emailAddress } onBlur={ onBlur("emailAddress") } dataPoint="Email_Address_Input" />
                    </FormRowView>
                    <FormRowView className="home-phone-row" text="Home Phone">
                        <UIPhoneNumberInput value={ userAccount.homePhoneNumber }
                        onChange={ onChangePhoneNumber("homePhoneNumber") } />
                    </FormRowView>
                    <FormRowView className="mobile-phone-row" text="Mobile Phone">
                        <UIPhoneNumberInput value={ userAccount.mobilePhoneNumber }
                        onChange={ onChangePhoneNumber("mobilePhoneNumber") } dataPoint="Mobile_Phone_Input" />
                    </FormRowView>
                    <FormRowView className="work-phone-row" text="Work Phone">
                        <HorizontalFormRow>
                            <UIPhoneNumberInput value={ userAccount.workPhoneNumber }
                            onChange={ onChangePhoneNumber("workPhoneNumber") } />
                            <span>ext.</span>
                            <div className="text-text-input-container ext-input-container">
                                <UITextInput value={ userAccount.workPhoneNumberExt } onChange={ onChange("workPhoneNumberExt") } />
                            </div>
                        </HorizontalFormRow>
                    </FormRowView>
                    <p className="sub-header">Home Address</p>
                    <FormRowView className="address-line-1-row" text="Address Line 1">
                        <UITextInput value={ userAccount.addressLine1 } onBlur={ onBlur("addressLine1") } />
                    </FormRowView>
                    <FormRowView className="address-line-2-row" text="Address Line 2">
                        <UITextInput value={ userAccount.addressLine2 } onBlur={ onBlur("addressLine2") } />
                    </FormRowView>
                    <FormRowView className="city-row" text="City">
                        <UITextInput value={ userAccount.city } onBlur={ onBlur("city") } />
                    </FormRowView>
                    <FormRowView className="state-row" text="State">
                        <StatesCustomSelect value={ userAccount.state } onBlur={ onBlur("state") }/>
                    </FormRowView>
                    <FormRowView className="zip-code-row" text="ZIP Code">
                        <UITextInput value={ userAccount.zip } onBlur={ onBlur("zip") } />
                    </FormRowView>
                </FormContainer>
            </div>
            <FormContainer className="button-container small-form-width">
                <FormButtonRow>
                    <SecondaryButton text="Cancel" onClick={ cancel } dataPoint="Cancel_Button" />
                    { data.isEditing && 
                    <DisabledPrompt>
                        <PrimaryButton text="Save" onClick={ onSave } dataPoint="Save_Button" />
                    </DisabledPrompt>
                    }
                    { !data.isEditing &&
                        <PrimaryButton text="Save" onClick={ onSave } dataPoint="Save_Button" />
                    }
                </FormButtonRow>
            </FormContainer>
        </CenterContentContainer>
    </PageContentView>
    );
  }
